/**
 * PricingRuleRewardWidget — calm, detail-native editor for the promotion's reward.
 * Replaces the wizard's form-style DiscountWidget on the detail page so it matches
 * the page's card/chip/white-input style (not a dense ERP form).
 *
 * One segmented type picker → the relevant value control:
 *   • ลด %        → discount_percentage
 *   • ลดเป็นเงิน   → discount_amount
 *   • ราคาพิเศษ    → rate  (fixed price)
 *   • แถมสินค้า    → free_item + free_qty (+ same_item)
 * Price modes also get "คิดจาก" (apply_discount_on) and "ส่วนลดสูงสุด" (max cap).
 * All staged via onFieldChange; the page's Save persists.
 */

import { Percent, Banknote, Tag, Gift } from "lucide-react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { NUM_INPUT_CLASS } from "./styles";

const pick = (th: boolean, t: string, e: string) => (th ? t : e);

type Kind = "pct" | "amt" | "rate" | "free";

export function PricingRuleRewardWidget({ formData, onFieldChange, disabled, isSaving }: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const th = language === "th";
  const ro = disabled || isSaving;
  const num = (v: unknown) => Number(v) || 0;

  const shipType = (formData.custom_shipping_discount_type as string) || "";
  const embroidery = !!formData.custom_is_embroidery_coupon;
  const setShipping = (t: string) => {
    onFieldChange("custom_shipping_discount_type", t);
    if (t !== "Shipping Discount") onFieldChange("custom_shipping_discount_value", 0);
  };

  const isProduct = formData.price_or_product_discount === "Product";
  const rod = (formData.rate_or_discount as string) || "";
  const kind: Kind = isProduct
    ? "free"
    : rod === "Discount Amount"
      ? "amt"
      : rod === "Rate"
        ? "rate"
        : "pct";

  const setKind = (k: Kind) => {
    if (k === "free") {
      onFieldChange("price_or_product_discount", "Product");
      return;
    }
    onFieldChange("price_or_product_discount", "Price");
    onFieldChange("rate_or_discount", k === "amt" ? "Discount Amount" : k === "rate" ? "Rate" : "Discount Percentage");
  };

  const types: { k: Kind; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
    { k: "pct", icon: Percent, label: pick(th, "ลด %", "% off") },
    { k: "amt", icon: Banknote, label: pick(th, "ลดเป็นเงิน", "฿ off") },
    { k: "rate", icon: Tag, label: pick(th, "ราคาพิเศษ", "Set price") },
    { k: "free", icon: Gift, label: pick(th, "แถมสินค้า", "Free item") },
  ];

  return (
    <div className="px-5 pt-6 pb-5">
      <SectionHeader label={pick(th, "รางวัลของโปรโมชัน", "The reward")} />

      <div className="mt-3 space-y-4 rounded-xl border border-border bg-card p-4 shadow-sm">
        {/* Type picker */}
        <div className="flex gap-1 rounded-lg border border-border bg-muted/40 p-1 dark:bg-white/[0.03]">
          {types.map(({ k, icon: Icon, label }) => (
            <button
              key={k}
              type="button"
              disabled={ro}
              onClick={() => setKind(k)}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 text-sm font-medium transition-colors",
                kind === k
                  ? "bg-white text-sky-700 shadow-sm dark:bg-gray-800 dark:text-sky-300"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </button>
          ))}
        </div>

        {/* Value control */}
        {kind === "pct" && (
          <ValueRow label={pick(th, "ลดกี่เปอร์เซ็นต์", "How many percent off")}>
            <input type="number" min={0} max={100} step={1} value={num(formData.discount_percentage) || ""} placeholder="0"
              onChange={(e) => onFieldChange("discount_percentage", Number(e.target.value) || 0)} disabled={ro} className={cn(NUM_INPUT_CLASS, "w-28")} />
            <span className="text-gray-500">%</span>
          </ValueRow>
        )}
        {kind === "amt" && (
          <ValueRow label={pick(th, "ลดกี่บาท", "How much off")}>
            <span className="text-gray-500">฿</span>
            <input type="number" min={0} step={1} value={num(formData.discount_amount) || ""} placeholder="0"
              onChange={(e) => onFieldChange("discount_amount", Number(e.target.value) || 0)} disabled={ro} className={cn(NUM_INPUT_CLASS, "w-28")} />
          </ValueRow>
        )}
        {kind === "rate" && (
          <ValueRow label={pick(th, "ตั้งราคาพิเศษเป็น", "Special price")}>
            <span className="text-gray-500">฿</span>
            <input type="number" min={0} step={1} value={num(formData.rate) || ""} placeholder="0"
              onChange={(e) => onFieldChange("rate", Number(e.target.value) || 0)} disabled={ro} className={cn(NUM_INPUT_CLASS, "w-28")} />
          </ValueRow>
        )}
        {kind === "free" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-5">
              <span className="text-sm text-gray-700 dark:text-gray-200">{pick(th, "แถมสินค้าตัวเดียวกับที่ซื้อ", "Give the same item they bought")}</span>
              <Switch checked={!!formData.same_item} onCheckedChange={(c) => onFieldChange("same_item", c ? 1 : 0)} disabled={ro} />
            </div>
            {!formData.same_item && (
              <div>
                <div className="mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">{pick(th, "สินค้าที่แถม", "Free item")}</div>
                <DocTypeSearchAutocomplete doctype="Item" value={(formData.free_item as string) || ""}
                  onChange={(v) => onFieldChange("free_item", v)} placeholder={pick(th, "เลือกสินค้าที่จะแถม…", "Item to give free…")}
                  minSearchLength={0} disabled={ro} />
              </div>
            )}
            <ValueRow label={pick(th, "จำนวนที่แถม", "Free quantity")}>
              <input type="number" min={0} step={1} value={num(formData.free_qty) || ""} placeholder="1"
                onChange={(e) => onFieldChange("free_qty", Number(e.target.value) || 0)} disabled={ro} className={cn(NUM_INPUT_CLASS, "w-28")} />
              <span className="text-gray-500">{pick(th, "ชิ้น", "items")}</span>
            </ValueRow>
          </div>
        )}

        {/* Price-mode extras: apply-on + max cap */}
        {kind !== "free" && (
          <div className="space-y-3 border-t border-border/50 pt-3">
            <ValueRow label={pick(th, "คิดส่วนลดจาก", "Apply discount to")}>
              <div className="flex gap-1.5">
                {[
                  { v: "Grand Total", label: pick(th, "ยอดรวมบิล", "Grand total") },
                  { v: "Net Total", label: pick(th, "ยอดสุทธิ", "Net total") },
                ].map((o) => {
                  const on = (formData.apply_discount_on || "Grand Total") === o.v;
                  return (
                    <button key={o.v} type="button" disabled={ro}
                      onClick={() => onFieldChange("apply_discount_on", o.v)}
                      className={cn(
                        "rounded-lg border px-2.5 py-1.5 text-sm font-medium transition-colors",
                        on ? "border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-300"
                          : "border-border bg-white text-gray-600 hover:border-sky-200 dark:bg-gray-900 dark:text-gray-300",
                      )}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </ValueRow>
            <ValueRow label={pick(th, "ส่วนลดสูงสุดต่อบิล", "Max discount per order")}>
              <span className="text-gray-500">฿</span>
              <input type="number" min={0} step={1} value={num(formData.max_discount_amount) || ""} placeholder={pick(th, "ไม่จำกัด", "No cap")}
                onChange={(e) => onFieldChange("max_discount_amount", Number(e.target.value) || 0)} disabled={ro} className={cn(NUM_INPUT_CLASS, "w-28")} />
            </ValueRow>
          </div>
        )}

        {/* Perks — free shipping / free personalization. For many coupons (e.g.
            PRLE-0091/0092) these ARE the reward; they add on top of any discount. */}
        <div className="mt-2 space-y-4 border-t border-border/60 pt-6">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{pick(th, "สิทธิพิเศษเพิ่มเติม", "Extra perks")}</div>
          <ValueRow label={pick(th, "ค่าจัดส่ง", "Shipping")}>
            <div className="flex gap-1.5">
              {[
                { v: "", label: pick(th, "ปกติ", "Normal") },
                { v: "Free Shipping", label: pick(th, "ส่งฟรี", "Free") },
                { v: "Shipping Discount", label: pick(th, "ลดค่าส่ง", "Discount") },
              ].map((o) => {
                const on = shipType === o.v;
                return (
                  <button key={o.v || "none"} type="button" disabled={ro} onClick={() => setShipping(o.v)}
                    className={cn(
                      "rounded-lg border px-2.5 py-1.5 text-sm font-medium transition-colors",
                      on ? "border-sky-300 bg-sky-50 text-sky-700 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-300"
                        : "border-border bg-white text-gray-600 hover:border-sky-200 dark:bg-gray-900 dark:text-gray-300",
                    )}>
                    {o.label}
                  </button>
                );
              })}
            </div>
          </ValueRow>
          {shipType === "Shipping Discount" && (
            <ValueRow label={pick(th, "ลดค่าส่งกี่บาท", "Shipping ฿ off")}>
              <span className="text-gray-500">฿</span>
              <input type="number" min={0} step={1} value={num(formData.custom_shipping_discount_value) || ""} placeholder="0"
                onChange={(e) => onFieldChange("custom_shipping_discount_value", Number(e.target.value) || 0)} disabled={ro} className={cn(NUM_INPUT_CLASS, "w-28")} />
            </ValueRow>
          )}
          <div className="flex items-center justify-between gap-5">
            <span className="text-sm text-gray-700 dark:text-gray-200">{pick(th, "ปรับแต่ง / ปักฟรี", "Free personalization / embroidery")}</span>
            <Switch checked={embroidery} onCheckedChange={(c) => onFieldChange("custom_is_embroidery_coupon", c ? 1 : 0)} disabled={ro} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Label-left / control-right row, matching the generic inline-field rows
 * (text-sm, foreground, normal weight, roomy) — the "Buying" row style. */
function ValueRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-5 py-1">
      <span className="shrink-0 text-sm text-gray-900 dark:text-gray-100">{label}</span>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}
